import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {
  catchError,
  defer,
  delay,
  filter,
  from,
  fromEventPattern,
  interval,
  map,
  mergeMap,
  Observable,
  of,
  race,
  switchMap,
  take,
  throwError,
  timeout,
  timer
} from 'rxjs';
import { MatrixService } from '../../services/matrix/matrix.service';
import { Room } from 'matrix-js-sdk/lib/models/room';
import { ClientEvent } from 'matrix-js-sdk';

export const clientGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const clientService = inject(MatrixService);
  const item = sessionStorage.getItem('keybackupinteraction');
  const redirectParam = encodeURIComponent(state.url);

  if (state.url.includes('/profile-settings')) {
    if (clientService.hasClient && item) return of(true);

    const isStandaloneProfile = !state.url.includes('/rooms/') || state.url.split('/').filter(Boolean).length <= 3;

    return defer(() => {
      if (!item) return of(router.parseUrl(`/login?redirect=${redirectParam}`));
      const roomId = isStandaloneProfile ? null : extractRoomId(state.url);

      return restoreSessionAndWaitForClient(clientService, router, isStandaloneProfile, roomId, item, redirectParam);
    });
  }

  if (!state.url.includes('/rooms/') && clientService.hasClient && item) {
    return of(true);
  }

  const roomId = extractRoomId(state.url);
  if (!roomId) {
    return navigateToLogin(router, redirectParam);
  }

  return restoreUserSession(clientService, router, roomId, item, redirectParam);
};

function extractRoomId(url: string): string | null {
  const match = new RegExp(/\/rooms\/([^/]+)/).exec(url);
  return match ? match[1] : null;
}

function restoreUserSession(
  clientService: MatrixService,
  router: Router,
  roomId: string,
  item: string | null,
  redirectParam: string
): Observable<boolean | UrlTree> {
  if (!item) {
    return of(router.parseUrl(`/login?redirect=${redirectParam}`));
  }
  return from(clientService.restoreSession(roomId)).pipe(
    switchMap(() => {
      if (!clientService.hasClient) {
        return of(router.parseUrl(`/login?redirect=${redirectParam}`));
      }

      return waitForRoom(clientService, roomId).pipe(
        take(1),
        switchMap(room => {
          const membership = room.getMyMembership();
          if (membership !== 'join') {
            return of(router.parseUrl('/not-found'));
          }

          return of(true);
        })
      );
    }),
    catchError(() => of(router.parseUrl(`/login?redirect=${redirectParam}`)))
  );
}

function waitForRoom(clientService: MatrixService, roomId: string, maxWaitMs = 2000): Observable<Room> {
  const existing = clientService.client.getRoom(roomId);
  if (existing) return of(existing);

  const room$ = fromEventPattern<Room>(
    h => clientService.client.on(ClientEvent.Room, h),
    h => clientService.client.off(ClientEvent.Room, h)
  ).pipe(
    filter((room: Room) => room?.roomId === roomId),
    take(1)
  );

  const timeout$ = timer(maxWaitMs).pipe(mergeMap(() => throwError(() => new Error('ROOM_TIMEOUT'))));

  return race(room$, timeout$);
}

function navigateToLogin(router: Router, redirectParam: string): Observable<UrlTree> {
  return of(router.parseUrl(`/login?redirect=${redirectParam}`));
}

function waitForClient(clientService: MatrixService, roomId?: string): Observable<boolean> {
  return interval(100).pipe(
    filter(() => {
      const clientReady = clientService.hasClient;
      const roomReady = roomId ? !!clientService.client.getRoom(roomId) : true;
      return clientReady && roomReady;
    }),
    take(1),
    timeout({
      each: 10000,
      with: () => throwError(() => new Error('Timeout waiting for room cache'))
    }),
    switchMap(() => of(true))
  );
}

function restoreSessionAndWaitForClient(
  clientService: MatrixService,
  router: Router,
  isStandaloneProfile: boolean,
  roomId: string | null,
  item: string | null,
  redirectParam: string
): Observable<boolean | UrlTree> {
  return from(clientService.restoreSession(roomId ?? '')).pipe(
    switchMap(() => {
      if (isStandaloneProfile) {
        return of(null).pipe(
          delay(650), // allow client to stabilize
          switchMap(() =>
            interval(100).pipe(
              filter(() => clientService.hasClient),
              take(1),
              timeout(5000)
            )
          )
        );
      }

      return waitForClient(clientService, roomId!);
    }),
    map(() => true),
    catchError(() =>
      of(router.parseUrl(item ? `/keybackup?redirect=${redirectParam}` : `/login?redirect=${redirectParam}`))
    )
  );
}
